/**
 * Shared DocumentReference builders for historical chart imports
 * and Forms & Files folder uploads (same FHIR shape).
 */

import type { DocumentReference, Patient, Practitioner, Reference } from '@medplum/fhirtypes';

import {
  DOCUMENT_CATEGORY_SYSTEM,
  DOCUMENT_TYPE_SYSTEM,
  EPISODE_OF_CARE_CONTEXT_EXT,
  HISTORICAL_MIGRATION_TAG_CODES,
  HISTORICAL_MIGRATION_TAG_SYSTEM,
  HISTORICAL_RECORD_IDENTIFIER_SYSTEM,
  SP_HISTORICAL_DOCUMENT_TYPE_CODES,
} from '../constants';
import { isPsychotherapyNote } from './document-reference';

const CONFIDENTIALITY_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality';

export interface HistoricalChartDocRefInput {
  patientId: string;
  patientDisplay?: string;
  episodeOfCareId?: string;
  /** DOCUMENT_TYPE_SYSTEM code (e.g. historical-progress-note). */
  documentTypeCode: string;
  documentTypeDisplay?: string;
  documentCategoryCode?: 'admin' | 'clinical' | 'addendum';
  title: string;
  originalDate: string;
  binaryId: string;
  contentType?: string;
  /** Idempotency value, e.g. `progress-note/123` or `upload/{filename}`. */
  identifierValue: string;
  /**
   * Defaults to {@link HISTORICAL_RECORD_IDENTIFIER_SYSTEM}.
   * BigQuery migrate-sp-history may pass the legacy SimplePractice system for idempotency.
   */
  identifierSystem?: string;
  authorRef?: Reference<Practitioner>;
  description?: string;
  /** When false, skip copying Patient meta.accounts (default: stamp accounts). */
  stampPatientAccounts?: boolean;
  patient?: Patient;
}

export interface HistoricalPsychotherapyPdfDocRefInput {
  patientId: string;
  patientDisplay?: string;
  authorRef: Reference<Practitioner>;
  binaryId: string;
  title?: string;
  originalDate: string;
  /** Idempotency value, e.g. `psychotherapy-bundle/{spClientId}`. */
  identifierValue: string;
  /** Defaults to {@link HISTORICAL_RECORD_IDENTIFIER_SYSTEM}. */
  identifierSystem?: string;
  description?: string;
  episodeOfCareId?: string;
}

function historicalTag(): NonNullable<DocumentReference['meta']>['tag'] {
  return [
    {
      system: HISTORICAL_MIGRATION_TAG_SYSTEM,
      code: HISTORICAL_MIGRATION_TAG_CODES.historical,
      display: 'Historical',
    },
  ];
}

function categoryCoding(code: 'admin' | 'clinical' | 'addendum'): DocumentReference['category'] {
  const display =
    code === 'admin' ? 'Administrative' : code === 'addendum' ? 'Addendum' : 'Clinical';
  return [
    {
      coding: [{ system: DOCUMENT_CATEGORY_SYSTEM, code, display }],
      text: display,
    },
  ];
}

function eocContext(episodeOfCareId: string): Pick<DocumentReference, 'context' | 'extension'> {
  const eocRef = `EpisodeOfCare/${episodeOfCareId}`;
  return {
    context: {
      encounter: [{ reference: eocRef }],
      related: [{ reference: eocRef }],
    },
    extension: [
      {
        url: EPISODE_OF_CARE_CONTEXT_EXT,
        valueReference: { reference: eocRef },
      },
    ],
  };
}

function patientAccountsMeta(patient?: Patient): Pick<NonNullable<DocumentReference['meta']>, 'accounts'> {
  if (patient?.meta?.accounts?.length) return { accounts: patient.meta.accounts };
  if (patient?.meta?.account) return { accounts: [patient.meta.account] };
  return {};
}

/**
 * Chart-visible historical PDF DocumentReference (Medical Record timeline).
 * Stamps historical tag + optional Patient accounts + EoC context.
 */
export function buildHistoricalChartDocumentReference(
  input: HistoricalChartDocRefInput,
): DocumentReference {
  const categoryCode = input.documentCategoryCode ?? 'clinical';
  const date = input.originalDate.includes('T')
    ? input.originalDate
    : `${input.originalDate}T12:00:00Z`;
  const eoc = input.episodeOfCareId ? eocContext(input.episodeOfCareId) : {};
  if (input.episodeOfCareId && eoc.context) {
    eoc.context.period = { start: input.originalDate.slice(0, 10) };
  }

  return {
    resourceType: 'DocumentReference',
    status: 'current',
    docStatus: 'final',
    identifier: [
      {
        system: input.identifierSystem ?? HISTORICAL_RECORD_IDENTIFIER_SYSTEM,
        value: input.identifierValue,
      },
    ],
    type: {
      coding: [
        {
          system: DOCUMENT_TYPE_SYSTEM,
          code: input.documentTypeCode,
          display: input.documentTypeDisplay ?? input.title,
        },
      ],
      text: input.title,
    },
    category: categoryCoding(categoryCode),
    subject: {
      reference: `Patient/${input.patientId}`,
      display: input.patientDisplay,
    },
    date,
    author: input.authorRef ? [input.authorRef] : undefined,
    description: input.description,
    content: [
      {
        attachment: {
          contentType: input.contentType ?? 'application/pdf',
          url: `Binary/${input.binaryId}`,
          title: input.title,
        },
      },
    ],
    ...eoc,
    meta: {
      tag: historicalTag(),
      ...(input.stampPatientAccounts !== false ? patientAccountsMeta(input.patient) : {}),
    },
  };
}

/**
 * Private historical psychotherapy PDF — native `psychotherapy-note` type so the
 * Psychotherapy Notes page finds it. No Patient accounts (off Medical Record compartment).
 * May include historical tag for provenance; chart bundle must exclude psychotherapy-note.
 */
export function buildHistoricalPsychotherapyPdfDocumentReference(
  input: HistoricalPsychotherapyPdfDocRefInput,
): DocumentReference {
  const title = input.title ?? 'Historical psychotherapy notes';
  const date = input.originalDate.includes('T')
    ? input.originalDate
    : `${input.originalDate}T12:00:00Z`;
  const eoc = input.episodeOfCareId ? eocContext(input.episodeOfCareId) : {};

  return {
    resourceType: 'DocumentReference',
    status: 'current',
    docStatus: 'preliminary',
    identifier: [
      {
        system: input.identifierSystem ?? HISTORICAL_RECORD_IDENTIFIER_SYSTEM,
        value: input.identifierValue,
      },
    ],
    type: {
      coding: [
        {
          system: DOCUMENT_TYPE_SYSTEM,
          code: 'psychotherapy-note',
          display: 'Psychotherapy note',
        },
      ],
      text: title,
    },
    category: categoryCoding('clinical'),
    subject: {
      reference: `Patient/${input.patientId}`,
      display: input.patientDisplay,
    },
    date,
    author: [input.authorRef],
    description: input.description ?? 'Imported historical psychotherapy notes',
    securityLabel: [
      {
        coding: [
          {
            system: CONFIDENTIALITY_SYSTEM,
            code: 'R',
            display: 'Restricted',
          },
        ],
      },
    ],
    content: [
      {
        attachment: {
          contentType: 'application/pdf',
          url: `Binary/${input.binaryId}`,
          title,
        },
      },
    ],
    ...eoc,
    meta: {
      tag: historicalTag(),
      // Intentionally omit accounts — author-scoped AccessPolicy only.
    },
  };
}

/** True when a DocRef is historical chart material (not private psychotherapy). */
export function isHistoricalChartDocumentReference(doc: DocumentReference): boolean {
  if (!doc.meta?.tag?.some(
    (t) =>
      t.system === HISTORICAL_MIGRATION_TAG_SYSTEM &&
      t.code === HISTORICAL_MIGRATION_TAG_CODES.historical,
  )) {
    return false;
  }
  return !isPsychotherapyNote(doc);
}

/** Default type code for untitled folder uploads. */
export const DEFAULT_HISTORICAL_UPLOAD_TYPE_CODE =
  SP_HISTORICAL_DOCUMENT_TYPE_CODES.uploadedDocument;
